import { Component, FocusEvent, ReactNode } from 'react';
import { Divider } from '@mui/material';
import { ObjectWithId } from '@synergy/models';
import debounce from 'lodash.debounce';

import { BaseAutocompleteSuggestionType, PoolAutocompleteList } from '../autocomplete-list/pool-autocomplete-list';

export interface PoolFormAutocompleteListProps<T, PT extends BaseAutocompleteSuggestionType> {
    id?: string;
    items?: T[];
    value?: PT;
    required?: boolean;
    error?: boolean;
    disabled?: boolean;
    readOnly?: boolean;
    footer?: ReactNode;
    label?: ReactNode;
    name?: string;
    clearInputOnSelect?: boolean;
    initialValue?: string;
    disablePreventOverflow?: boolean;
    adornment?: ReactNode;
    inProgress?: boolean;
    placeholder?: string;
    /** Shown in place of the empty list when a search found nothing. */
    emptyMessage?: ReactNode;
    autoFocus?: boolean;
    helperText?: JSX.Element | null;
    inputRef?: (ref: HTMLInputElement) => void;
    onSelected?: (value: T | null) => void;
    onBlur?: (event: FocusEvent<HTMLInputElement>) => void;
    onValueChanged?: (value: string) => void;
}

type FunctionOrNode = (() => ReactNode) | ReactNode;

interface PoolFormAutocompleteListState<T, PT> {
    suggestionData: PT[];
    items?: T[];
    searching?: boolean;
}

// Generics needed for common interface with abstract base class
/* eslint-disable @typescript-eslint/no-unused-vars */
export interface PoolFormAutocompleteList<
    T extends ObjectWithId,
    PT extends BaseAutocompleteSuggestionType,
    P = Record<string, unknown>
> {
    handleValueChanged?(value: string): void;
    getDisplayValue?(suggestion: BaseAutocompleteSuggestionType): string;
}
/* eslint-enable */

// eslint-disable-next-line @typescript-eslint/no-unsafe-declaration-merging
export abstract class PoolFormAutocompleteList<
        T extends ObjectWithId,
        PT extends BaseAutocompleteSuggestionType,
        P = Record<string, unknown>
    >
    extends Component<P & PoolFormAutocompleteListProps<T, PT>, PoolFormAutocompleteListState<T, PT>>
    implements PoolFormAutocompleteList<T, PT, P>
{
    public footer?: FunctionOrNode = undefined;
    public label?: FunctionOrNode = undefined;
    public resetSearch?: () => void = undefined;
    public abstract getSuggestionValue: (value: PT) => string;
    public abstract getPartial: (items: T[]) => PT[];
    public hideId = false;
    public adornment?: ReactNode;
    public alwaysShowSuggestions = true;
    public getDetailContent?: (item?: PT) => ReactNode;
    /** Debounced + gated wrapper around handleValueChanged; drives the spinner. */
    public searchValue?: (value: string) => void;

    public state: PoolFormAutocompleteListState<T, PT> = {
        suggestionData: [],
        searching: false,
    };

    constructor(props: P & PoolFormAutocompleteListProps<T, PT>) {
        super(props);
        this.handleSelected = this.handleSelected.bind(this);
        if (this.handleValueChanged) {
            const fetchSuggestions = debounce(this.handleValueChanged.bind(this), 250);
            this.searchValue = (value: string) => {
                // Skip near-useless single-character queries; keep empty (browse) and 2+.
                if (value.trim().length === 1) {
                    fetchSuggestions.cancel();
                    if (this.state.searching) {
                        this.setState({ searching: false });
                    }
                    return;
                }
                if (!this.state.searching) {
                    this.setState({ searching: true });
                }
                fetchSuggestions(value);
            };
        }
    }

    public handleSelected(value: BaseAutocompleteSuggestionType | null) {
        if (this.props.onSelected) {
            if (value) {
                const id = typeof value.id === 'number' ? value.id : parseInt(value.id, 10);
                const selectedItem = this.getItems().find(item => item.id === id);
                this.props.onSelected(selectedItem || null);
            } else {
                this.props.onSelected(null);
            }
        }
        if (this.resetSearch) {
            this.resetSearch();
        }
    }

    private getNode(node?: FunctionOrNode): ReactNode | undefined {
        if (!node) {
            return undefined;
        }
        if (node instanceof Function) {
            return node();
        }
        return node;
    }

    // Prefer items from local state - if undefined take from props
    public getItems = (props?: PoolFormAutocompleteListProps<T, PT>, state?: PoolFormAutocompleteListState<T, PT>) => {
        return (state || this.state).items || (props || this.props).items || [];
    };

    public componentDidMount() {
        this.setState({ suggestionData: this.getPartial(this.props.items ?? []) });
    }

    public componentWillUpdate(
        nextProps: P & PoolFormAutocompleteListProps<T, PT>,
        nextState: PoolFormAutocompleteListState<T, PT>
    ) {
        if (nextProps.items !== this.props.items || nextState.items !== this.state.items) {
            // Results arrived — also stop the spinner here rather than in
            // componentDidUpdate: several subclasses override that hook without
            // calling super, which left the spinner running forever.
            this.setState({
                suggestionData: this.getPartial(this.getItems(nextProps, nextState)),
                searching: false,
            });
        }
    }

    public render() {
        const {
            id,
            value,
            required,
            error,
            disabled,
            readOnly,
            initialValue,
            clearInputOnSelect,
            disablePreventOverflow,
            inputRef,
            footer,
            label,
            onValueChanged,
            adornment,
            inProgress,
            placeholder,
            helperText,
        } = this.props;

        const detailComponent = this.getDetailContent?.(value);

        const adornments = [value ? detailComponent : null, adornment]
            .filter(item => item)
            .map((item, index, items) => {
                if (index < items.length - 1) {
                    return [item, <Divider orientation="vertical" style={{ height: 24 }} />];
                }
                return item;
            })
            .flat();

        return (
            <PoolAutocompleteList
                id={id || ''}
                name={this.props.name}
                initialSuggestionValue={value}
                data={this.state.suggestionData}
                getSuggestionValue={suggestion => this.getSuggestionValue(suggestion as PT)}
                handleSuggestionSelected={this.handleSelected}
                footer={footer || this.getNode(this.footer)}
                label={label || this.getNode(this.label)}
                onValueChanged={onValueChanged || this.searchValue}
                // Passed through
                getDisplayValue={this.getDisplayValue}
                required={required}
                error={error}
                disabled={disabled}
                readOnly={readOnly}
                initialValue={initialValue}
                clearInputOnSelect={clearInputOnSelect}
                inputRef={inputRef}
                disablePreventOverflow={disablePreventOverflow}
                exclude={(this.hideId && ['id']) || undefined}
                adornment={adornments.length > 0 ? adornments : null}
                alwaysShowSuggestions={this.alwaysShowSuggestions}
                showProgress={inProgress || this.state.searching}
                placeholder={placeholder}
                emptyMessage={this.props.emptyMessage}
                autoFocus={this.props.autoFocus}
                helperText={helperText}
            />
        );
    }
}
